import {useAuth} from '@apps-common/auth-react';
import {useGetUserById} from '@src/api/ims.ts';
import {DataCollectionsRoutePattern} from '@src/constants/routes.ts';
import {useHasImpersonationRights} from '@src/hooks/dataCollections/impersonation/useHasImpersonationRights.ts';
import useImpersonationParams from '@src/hooks/queryParams/dataCollections/useImpersonationParams.ts';
import {useCallback} from 'react';
import {useLocation, useNavigate} from 'react-router';

export const useImpersonationAwareAuth = () => {
  const auth = useAuth();
  const {pathname} = useLocation();
  const navigate = useNavigate();
  const {impersonatedUserId, impersonatedProjectId} = useImpersonationParams();

  const stopImpersonating = useCallback(() => {
    if (impersonatedUserId === null || impersonatedProjectId === null) {
      return;
    }

    navigate(DataCollectionsRoutePattern.root);
  }, [impersonatedUserId, impersonatedProjectId, navigate]);

  const isInDataCollections = pathname.startsWith(
    DataCollectionsRoutePattern.root,
  );
  const isImpersonating =
    isInDataCollections &&
    impersonatedUserId !== null &&
    impersonatedProjectId !== null;

  const {hasImpersonationRights, isLoading: isImpersonationRightsLoading} =
    useHasImpersonationRights({enabled: isImpersonating});

  const isImpersonationDenied =
    isImpersonating && !isImpersonationRightsLoading && !hasImpersonationRights;

  const {data: impersonatedUser, isLoading: isImpersonatedUserLoading} =
    useGetUserById(impersonatedUserId ?? '', {
      enabled: isImpersonating && hasImpersonationRights,
    });

  const impersonation = isImpersonating
    ? {
        isImpersonating: true as const,
        impersonatedUserId,
        impersonatedProjectId,
        impersonatedUser,
        hasImpersonationRights,
        isImpersonationDenied,
        isLoading: isImpersonationRightsLoading || isImpersonatedUserLoading,
      }
    : {isImpersonating: false as const};

  const baseClaims = auth.user?.claims;

  const claims = {
    ...baseClaims,
    userId: impersonation.isImpersonating
      ? impersonation.impersonatedUserId
      : baseClaims?.sh_user_id,
    projectId: impersonation.isImpersonating
      ? impersonation.impersonatedProjectId
      : baseClaims?.pl_project,
  };

  return {
    ...auth,
    user: {
      ...auth.user,
      claims,
    },
    impersonation,
    stopImpersonating,
  };
};
